import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ArpsTableRow,
  headersArpsCustomTable,
  valoresExibidosOffCanvasArps,
} from './auxiliarArps';
import BarraInferiorBotoes from '../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../components/BarraSubAbas';
import BoxConteudo from '../../../../components/BoxConteudo';
import ContainerConteudo from '../../../../components/ContainerConteudo';
import CustomCol from '../../../../components/CustomCol';
import { CustomSwal } from '../../../../components/CustomFeedback/CustomSwal';
import CustomOffcanvas from '../../../../components/CustomOffcanvas';
import CustomRow from '../../../../components/CustomRow';
import DetailsTable from '../../../../components/CustomTable/DetailsTable';
import CustomTableOffcanvas from '../../../../components/CustomTableOffcanvas';
import SelectInput from '../../../../components/InputNew/Select';
import WorkspaceBotoes from '../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../components/WorkspaceFicha';
import { getUser } from '../../../../modules/contexts/Auth/RequireAuth';
import { useSelect } from '../../../../modules/contexts/Select/useSelect';
import { useFornecedoresCache } from '../../../../services/cache/useFornecedoresCache';
import { getArpsByUsuario } from '../../../../services/contratos/arpsService';
import { getProaqsByUsuario } from '../../../../services/processosAquisitivos/proaqService';
import { useExcelExport } from '../../../../utils/excel-export';
import {
  formataNomeMeioSiglas,
  formatarNumero,
  formatDate,
  formatDateSimp,
  formatoMoney,
} from '../../../../utils/functions';
import { SubAbas as subAbas } from '../subabas';

const ContratosArpsIndex: React.FC = () => {
  //DADOS USUARIO
  const user = getUser();
  const userComponenteAFLabel =
    user?.vinculo?.[0]?.nomeInstituicao ?? 'Não informado';
  const userComponenteAF = Number(user.vinculo[0].numeroComponenteAF);

  const navigate = useNavigate();

  // GETS
  const { getSelectDominioPai } = useSelect();
  const statusVigenciaOptions = getSelectDominioPai(68);
  const componenteAFoptions = getSelectDominioPai(1);
  const tipoCotaArpOptions = getSelectDominioPai(72);

  const tituloModal = 'Nova Ata de Registro de Preço';

  // STATES

  const [data, setData] = useState<any[]>([]);
  const [proaqData, setProaqData] = useState<any[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [statusInserirNewArp, setStatusInserirNewArp] = useState<boolean>(true);
  const { exportData, isExporting } = useExcelExport('arp');
  const [formNewArp, setFormNewArp] = useState({
    proaqId: 0,
    proaqLabel: '',
    proaqItem: '',
    fornecedorId: 0,
    kitId: 0,
    denominacaoId: 0,
    componenteAf: userComponenteAF,
  });

  // FETCH

  const fetchProaqs = async () => {
    try {
      const response = await getProaqsByUsuario();
      // Filtra os registros
      const filtrados = response.filter(
        (item: any) => item.submodalidade === 140,
      );

      // Mapeia para options do Select
      const options = filtrados.map((item: any) => ({
        value: item.id,
        label:
          'ID: ' +
          item.id +
          ' - NUP: ' +
          item.numeroSeiProaq +
          ' - ' +
          (item.denominacaoGenerica ? '(D)' : '(K)') +
          ' ' +
          (item.denominacaoGenerica?.denominacaoGenerica ||
            item.KitProdutoDAF?.nomeKit) +
          ' (' +
          (item.denominacaoGenerica?.id || item.KitProdutoDAF?.id) +
          ')',
      }));

      // Atualiza o state
      setProaqData([{ value: 0, label: 'Não informado' }, ...options]);
    } catch (error) {
      console.error('Erro ao buscar processos aquisitivos:', error);
    }
  };

  const { data: fornecedores, loading: loadingFornecedores } =
    useFornecedoresCache();
  const fornecedoresOptions = [
    { value: 0, label: 'Não informado' },
    ...fornecedores.map((item) => ({
      value: item.id,
      label: item.cnpj + ' - ' + item.nomeFantasia,
    })),
  ];

  const fetchData = async () => {
    try {
      const registros = await getArpsByUsuario();
      setData(registros);
    } catch (error) {
      console.error('Erro ao obter dados:', error);
    }
  };

  // USE EFFECT
  useEffect(() => {
    fetchProaqs();
    fetchData();
  }, []);

  useEffect(() => {
    // Verifica se as condições são satisfeitas
    if (formNewArp.proaqId === 0 || formNewArp.fornecedorId === 0) {
      setStatusInserirNewArp(true);
    } else {
      setStatusInserirNewArp(false);
    }
  }, [formNewArp]);

  const safeToFixed = (value: any, decimals = 2) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(decimals) : '0.00';
  };

  // TABELA E OFFCANVAS
  const tableData = data.map((item) => ({
    id: item?.id ?? 'Não informado',

    status:
      statusVigenciaOptions.find((status) => status.value === item.statusArp)
        ?.label ?? 'Não informado',
    componenteAf:
      componenteAFoptions.find(
        (componenteAf) => componenteAf.value === item.componenteAf,
      )?.label ?? 'Não informado',
    numeroArp: item?.numeroArp ?? 'Não informado',
    fornecedor:
      '[' + item.fornecedor.cnpj + '] - ' + item.fornecedor.nomeFantasia + ']',
    denominacaoKit:
      item?.denominacaoGenerica.denominacaoGenerica ??
      item?.KitProdutoDAF?.nomeKit ??
      'Não informado',
    denominacaoKitBool: item?.denominacaoGenerica ? 'D' : 'K',
  }));

  const offCanvasData = data.map((item) => {
    // Definindo cores de fundo com base no status do documento
    let statusBackgroundColor = '';
    let colorText = 'black';

    const statusLabel = statusVigenciaOptions.find(
      (option) => option.value === item.statusArp,
    )?.label;

    if (statusLabel === 'Não Publicado') {
      statusBackgroundColor = '#fff89a';
    } else if (statusLabel === 'Vigente') {
      statusBackgroundColor = '#008d00a7';
      colorText = 'white';
    } else if (statusLabel === 'Encerrado') {
      statusBackgroundColor = '#757575';
    } else if (statusLabel === 'Suspenso') {
      statusBackgroundColor = '#ffc9b4dd';
    } else if (statusLabel === 'Cancelado') {
      statusBackgroundColor = '#ff8a8ac3';
    }

    // Calculando data de vigência
    let formattedDataVigencia = '';
    let diferencaEmDias = '';

    if (item.dataPublicacao) {
      const dataVigencia = new Date(item.dataPublicacao);
      dataVigencia.setDate(dataVigencia.getDate() + 365);

      formattedDataVigencia = formatDateSimp(dataVigencia.toISOString());

      const hoje = new Date();
      const dias = Math.ceil(
        (dataVigencia.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24),
      );
      diferencaEmDias = dias.toString();
    }

    const itensAdquiridosArray = Array.isArray(item.itensAdquiridos)
      ? item.itensAdquiridos
      : [];

    // Mapeando os itens adquiridos em formato de bulletpoints
    const itensAdquiridos = (
      <ul style={{ paddingLeft: '20px' }}>
        {itensAdquiridosArray.map((produto: any) => {
          return (
            <li key={produto.id}>
              <strong>{produto.produtoNome}</strong>
              <br></br>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  minWidth: '300px',
                }}
              >
                <span>Tipo de Cota:</span>
                <span style={{ textAlign: 'right' }}>
                  {
                    tipoCotaArpOptions.find(
                      (option) => option.value === produto.tipoCotaArp,
                    )?.label
                  }
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  minWidth: '300px',
                }}
              >
                <span>Qtd Registrada:</span>
                <span style={{ textAlign: 'right' }}>
                  {formatarNumero(produto.qtdTotal.toString())}
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  minWidth: '300px',
                }}
              >
                <span>Valor Homologado:</span>
                <span style={{ textAlign: 'right' }}>
                  {formatoMoney(
                    (produto.valorUnitHomologado / 10000).toString(),
                  )}
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  minWidth: '300px',
                }}
              >
                <span>Valor Reequilíbrio:</span>
                <span style={{ textAlign: 'right' }}>
                  {produto.valorReequilibrioBin
                    ? formatoMoney(
                        (produto.valorUnitReequilibrio / 10000).toString(),
                      )
                    : '-'}
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  minWidth: '300px',
                }}
              >
                <span>Valor Total:</span>
                <span style={{ textAlign: 'right' }}>
                  {formatoMoney(safeToFixed(produto.valorTotal))}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    );

    return {
      id: item.id,
      dataRegistro:
        formatDate(item.dataRegistro) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioRegistro?.nomeCompleto ?? ''),
      dataUltimaAtualizacao:
        formatDate(item.dataUltimaAtualizacao) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioAtualizacao?.nomeCompleto ?? ''),

      dataUltimaVisualizacao:
        formatDate(item.dataUltimaVisualizacao) +
        ' - ' +
        formataNomeMeioSiglas(
          item.usuarioUltimaVisualizacao?.nomeCompleto ?? '',
        ),
      nEdicoes: item.nEdicoes,
      nVisualizacoes: item.nVisualizacoes,
      separadorLog: '',
      statusArp: (
        <span
          style={{
            backgroundColor: statusBackgroundColor,
            color: colorText,
            borderRadius: '10px',
            padding: '10px',
            display: 'inline-block',
            minWidth: '100px',
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          {statusLabel}
        </span>
      ),
      componenteAf: componenteAFoptions.find(
        (option) => option.value === item.componenteAf,
      )?.label,
      itemAdquirido: (
        <b style={{ fontSize: '1.25rem' }}>{item.itemAdquirido}</b>
      ),
      fornecedorNomeFantasia: item.fornecedor.nomeFantasia,
      leiLicitacao: item.leiLicitacao,
      numeroSeiArp: item.numeroSeiArp,
      numeroArp: item.numeroArp,
      dataPublicacao: formatDateSimp(item.dataPublicacao),
      dataVigencia: formattedDataVigencia || '-',
      prazoVigencia: diferencaEmDias || '-',
      nItens: item.nItensArp,
      valorTotal: formatoMoney(safeToFixed(item.valorTotalArp)),
      separadorItens: '',
      itensAdquiridos: itensAdquiridos,
      separadorObservacoes: '',
      observacoes: item.observacoes,
    };
  });

  // FUNÇÕES HANDLE
  const handleClearModal = () => {
    setFormNewArp({
      ...formNewArp,
      proaqId: 0,
      proaqLabel: '',
      proaqItem: '',
      fornecedorId: 0,
    });
  };

  const handleShowModal = () => {
    handleClearModal();
    setShowModal((show) => !show);
  };

  const handleClickModalInserir = () => {
    if (
      ['Gabinete', 'Coordenação de Fiscalização de Contratos'].includes(
        userComponenteAFLabel,
      )
    ) {
      CustomSwal.show({
        type: 'warning',
        title: 'Atenção',
        html: 'Usuários vinculados ao Gabinete ou à Coordenação de Fiscalização de Contratos não podem criar ARPs.',
        showConfirmButton: true,
      });
      return;
    }
    navigate('dadosgerais', { state: { formNewArp } });
  };

  const handleChangeModal = (e: any) => {
    const { name, value } = e.target;
    const newValue = parseInt(value, 10);

    setFormNewArp((prev) => {
      const updatedForm = { ...prev, [name]: newValue };

      if (name === 'proaqId') {
        const selectedProaq = proaqData.find((item) => item.value === newValue);

        if (selectedProaq) {
          // Extrair NUP
          const match = selectedProaq.label.match(/NUP:\s*(.*?)\s*- \(/);
          updatedForm.proaqLabel = match ? match[1].trim() : '';

          // Extrair itemId entre parênteses no final do label
          const matchItemId = selectedProaq.label.match(/\((\d+)\)\s*$/);
          const itemId = matchItemId ? parseInt(matchItemId[1], 10) : 0;

          // Verificar tipo do item: (D) ou (K)
          if (selectedProaq.label.includes('(D)')) {
            updatedForm.denominacaoId = itemId;
            updatedForm.kitId = 0;
          } else if (selectedProaq.label.includes('(K)')) {
            updatedForm.kitId = itemId;
            updatedForm.denominacaoId = 0;
          } else {
            // Caso não encontre nenhuma das duas marcações, zera ambos por segurança
            updatedForm.kitId = 0;
            updatedForm.denominacaoId = 0;
          }
        } else {
          updatedForm.proaqLabel = '';
          updatedForm.kitId = 0;
          updatedForm.denominacaoId = 0;
        }
      }

      return updatedForm;
    });
  };

  // const handleExport = async () => {
  //   try {
  //     await exportData(
  //       data, // dados do módulo
  //       `arps_${formatDateToFile(new Date().toISOString())}.xlsx`,
  //       userID,
  //       'ARP', // nome da entidade
  //       'DAF', // nome da interface
  //       {
  //         componenteAf,
  //         statusArp,
  //         tipoCotaArp,
  //         leiLicitacao,
  //       },
  //     );
  //   } catch (error) {
  //     console.error('Erro na exportação:', error);
  //   }
  // };

  const [selectedDetailsRowId, setSelectedDetailsRowId] = useState<
    string | number | null
  >(null);
  const offcanvasOpenRoute = useMemo(() => {
    if (!selectedDetailsRowId) return undefined;

    const baseHash = window.location.hash.replace(/\/$/, '');
    return `${baseHash}/${selectedDetailsRowId}/dadosgerais`;
  }, [selectedDetailsRowId]);

  const handleOpenDetails = (row: ArpsTableRow) => {
    if (!row?.id) return;
    setSelectedDetailsRowId(row.id);
  };

  const handleTableRowClick = (row: ArpsTableRow) => {
    if (row?.id) {
      navigate(`${row.id}/dadosgerais`);
    }
  };

  return (
    <>
      <BarraSubAbas subAbas={subAbas} />

      <ContainerConteudo>
        <CustomTableOffcanvas
          headers={headersArpsCustomTable}
          data={tableData}
          displayValues={valoresExibidosOffCanvasArps}
          offcanvasTitle="ARPs - Detalhes"
          widthOffcanvas="50%"
          openRoute={offcanvasOpenRoute}
          onOpenOffcanvas={handleOpenDetails}
          onRowClick={handleTableRowClick}
          itemsPerPage={50}
          // emptyMessage="Carregando..."
          showPagination={true}
          showOpenButton={true}
          filterable={true}
        >
          {(row) => (
            <DetailsTable
              rows={offCanvasData}
              selectedId={row?.id ?? selectedDetailsRowId}
              displayValues={valoresExibidosOffCanvasArps}
            />
          )}
        </CustomTableOffcanvas>

        {/* Inserir nova ARP */}
        <CustomOffcanvas
          title={tituloModal}
          show={showModal}
          onClose={handleShowModal}
          width="70vw"
        >
          <WorkspaceFicha>
            <BoxConteudo boxTitulo="Dados da Nova ARP">
              <CustomRow>
                <CustomCol>
                  <SelectInput
                    name="proaqId"
                    value={formNewArp.proaqId}
                    options={proaqData}
                    onChange={handleChangeModal}
                    label="Processo Aquisitivo"
                  />
                </CustomCol>
              </CustomRow>
              <CustomRow>
                <CustomCol>
                  <SelectInput
                    name="fornecedorId"
                    value={formNewArp.fornecedorId}
                    options={fornecedoresOptions}
                    onChange={handleChangeModal}
                    label="Fornecedor"
                    disabled={formNewArp.proaqId === 0 ? true : false}
                  />
                </CustomCol>
              </CustomRow>
            </BoxConteudo>

            <BarraInferiorBotoes>
              <WorkspaceBotoes
                inserir
                inserirDisabled={statusInserirNewArp}
                onClickInserir={handleClickModalInserir}
              />
            </BarraInferiorBotoes>
          </WorkspaceFicha>
        </CustomOffcanvas>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          downloadDisabled={isExporting || !(tableData.length > 0)}
          inserir
          onClickInserir={handleShowModal}
        />
      </BarraInferiorBotoes>
    </>
  );
};

export default ContratosArpsIndex;
